import React, { useState, useEffect } from 'react';
import './CouponDescription.css';
import { useNavigate, useLocation } from 'react-router-dom';
import en from '../locales/eng.json';
import th from '../locales/th.json';
import CONFIG from '../config';

import getProfileImage from '../utils/getProfileImage';

function CouponDescription() {
  const navigate = useNavigate();
  const location = useLocation();

  // Show-menu toggle was missing
  const [showMenu, setShowMenu] = useState(false);

  // Localization
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'TH';
  });
  const [t, setT] = useState(language === 'EN' ? en : th);

  // Coupon and user data
  const queryParams = new URLSearchParams(location.search);
  const couponId = queryParams.get('coupon_id');
  const [coupon, setCoupon] = useState(null);
  const [userData, setUserData] = useState(null);

  // Loading / success / popup states
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showErrorTxt, setShowErrorTxt] = useState("");

  // Profile image utility
  const profileImage = getProfileImage();

  // Update localization when `language` changes
  useEffect(() => {
    localStorage.setItem('language', language);
    setT(language === 'EN' ? en : th);
  }, [language]);

  // Fetch coupon details
  useEffect(() => {
    const fetchCoupon = async () => {
      try {
        const res = await fetch(
          `${CONFIG.API_BASE_URL}/coupon/?coupon_id=${couponId}&page=1&limit=1`
        );
        const result = await res.json();
        if (result?.content?.data?.length > 0) {
          setCoupon(result.content.data[0]);
        }
      } catch (err) {
        console.error('Failed to fetch coupon:', err);
      }
    };
    if (couponId) fetchCoupon();
  }, [couponId]);

  // Fetch user data (using line_user_id from localStorage)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const lineUserId = localStorage.getItem('line_user_id');
        if (!lineUserId) throw new Error('No line_user_id found');

        const res = await fetch(
          `${CONFIG.API_BASE_URL}/lineuser/fetch_user_data?line_user_id=${lineUserId}`
        );
        const result = await res.json();
        if (result?.content?.data) {
          setUserData(result.content.data);
        } else {
          throw new Error('Invalid user data format');
        }
        setIsLoading(false);
      } catch (e) {
        console.error('Failed to fetch user data:', e);
        navigate('/'); // redirect on error
      }
    };

    fetchUserData();
  }, [navigate]);

  // Redeem coupon handler
  const handleRedeemCoupon = async () => {
    if (!userData) return;
    setIsLoading(true);

    if(userData.point - coupon?.point_cost  < 0) {
      setShowErrorTxt(t.coupon_description.not_enough_points);
      setIsError(true);
      setIsLoading(false);
      return;
    }

    console.log('Redeeming coupon:', userData.point - coupon?.point_cost, 'for user:', userData);

    try {
      const res = await fetch(
        `${CONFIG.API_BASE_URL}/coupon/redeem_coupon?line_user_id=${userData.line_user_id}&coupon_id=${couponId}&detail=แลกคูปอง`,
        { method: 'POST' }
      );
      const result = await res.json();
      
      if (res.ok) {
        setIsSuccess(true);
        setShowPopup(false);
      } else {
        // alert('Failed to redeem coupon: ' + (result.message || 'Unknown error'));
        setShowErrorTxt(t.coupon_description.full_coupon);
        setIsError(true);
      }
    } catch (err) {
      console.error('Redeem error:', err);
      setShowErrorTxt(t.coupon_description.error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    navigate('/my-coupon');
  };


  return (
    <div style={{ backgroundColor: '#FFFFFF' }}>

      {isLoading && (
        <div className="full-screen-spinner">
          <div className="spinner" />
        </div>
      )}

      {/* ===== HEADER ===== */}
      <div className="point-header-gradient">
        <img
          onClick={() => navigate('/')}
          src="/assets/logo-ujoy.png"
          alt="logo"
          className="logo-ujoy"
        />
        <div className="right-section">
          <div className="lang-selector">
            <button
              className={`lang-button ${language === 'TH' ? 'active' : ''}`}
              onClick={() => setLanguage('TH')}
            >
              TH
            </button>
            |
            <button
              className={`lang-button ${language === 'EN' ? 'active' : ''}`}
              onClick={() => setLanguage('EN')}
            >
              EN
            </button>
          </div>

          {/* Profile avatar (toggles menu) */}
          <div>
            <img
              onClick={() => navigate('/profile')}
              src={userData?.line_image_url || '/assets/avartar.png'}
              alt="avatar"
              className="profile-avatar"
            />
          </div>

          {/* ===== Profile menu (conditionally shown) ===== */}
          {showMenu && (
            <>
              <div className="menu-overlay" onClick={() => setShowMenu(false)} />
              <div className="menu-popup-wrapper">
                <div className="menu-popup">
                  <div
                    className="menu-item"
                    onClick={() => {
                      navigate('/my-coupon');
                      setShowMenu(false);
                    }}
                  >
                    <img src="/assets/icon-coupon.png" alt="coupon" />
                    <span>คูปองของฉัน</span>
                  </div>
                  <div
                    className="menu-item"
                    onClick={() => {
                      navigate('/point-redeem');
                      setShowMenu(false);
                    }}
                  >
                    <img src="/assets/icon-redeem.png" alt="redeem" />
                    <span>แลกคะแนน</span>
                  </div>
                  <hr className="dashed-line" />
                  <div
                    className="menu-item"
                    onClick={() => {
                      navigate('/point-history');
                      setShowMenu(false);
                    }}
                  >
                    <img src="/assets/icon-points.png" alt="points" />
                    <span>คะแนนสะสม</span>
                  </div>
                  <hr className="dashed-line" />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ===== TITLE BAR ===== */}
      <div className="point-header-bar">
        <button className="back-button" onClick={() => navigate(-1)}>
          <img src="/assets/arrow_back.png" alt="Back" className="back-icon" />
        </button>
        <span className="point-header-title">{t.coupon_description.coupon_description}</span>
      </div>

      {/* ===== COUPON DETAIL ===== */}
      <div className="coupon-detail-container">
        <div className="coupon-image-wrapper-main">
          <img
            src={coupon?.image_url || '/assets/default-coupon.png'}
            
            alt={coupon?.name || 'coupon'}
            className="coupon-image-main"
          />
        </div>

        <div className="coupon-tag-box">
          <div className="coupon-points">
            <span style={{ color: '#00C3AA', fontWeight: 'bold', fontSize: '16px' }}>
              {coupon?.point_cost ?? ''}
            </span>
            <img src="/assets/coin.png" alt="coin" style={{ width: '30px', marginLeft: 2 }} />
          </div>
          <div className="coupon-expiry">
            <span style={{ fontSize: '12px', color: '#999' }}>from batch expiring on</span>
            <br />
            <span style={{ fontSize: '12px', color: '#999' }}>
              {coupon?.expired_at
                ? new Date(coupon.expired_at).toLocaleDateString()
                : ''}
            </span>
          </div>
        </div>

        <h3 className="coupon-title-main">{
language === 'EN' ? coupon?.name_en || '' : coupon?.name || ''
      }</h3>
        <br />
        <div className="coupon-description">
           {language === 'EN' ? <> { (coupon?.description_en || '').split('\n').map((line, i) => (
            <p key={i} className="coupon-text">
            {line}
            </p>
          )) }</> : <> { (coupon?.description || '').split('\n').map((line, i) => (
            <p key={i} className="coupon-text">
            {line}
            </p>
          )) }</>}
        </div>

        <button
          className="redeem-button-full"
          onClick={() => setShowPopup(true)}
        >
          {t.coupon_description.redeem}
        </button>
      </div>

      {/* ===== CONFIRM REDEEM POPUP ===== */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h3 style={{ color: '#037887', fontWeight: 'bold' }}>
              {t.coupon_description.confirm_redeem}
            </h3>
            <p style={{ color: '#7975E9', fontWeight: 'bold' }}>{coupon?.name}</p>
            <div className="popup-summary">
              <span>{t.coupon_description.total}</span>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: '#00C3AA',
                  fontWeight: 'bold',
                }}
              >
                {coupon?.point_cost ?? ''}
                <img
                  src="/assets/coin.png"
                  alt="coin"
                  style={{ width: '25px', marginLeft: '6px' }}
                />
              </div>
            </div>
            <div className="popup-buttons">
              <button
                className="cancel-btn"
                onClick={() => setShowPopup(false)}
              >
                {t.coupon_description.cancel}
              </button>
              <button
                className="confirm-btn"
                onClick={handleRedeemCoupon}
              >
                {t.coupon_description.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SUCCESS MODAL ===== */}
      {isSuccess && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h3 style={{ color: '#037887', fontWeight: 'bold' }}>
              {t.coupon_description.redeemed_successfully}
            </h3>
            <div className="popup-buttons">
              <button
                className="confirm-btn"
                onClick={handleCloseSuccessModal}
              >
                {t.coupon_description.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {isError && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h3 style={{ color: '#037887', fontWeight: 'bold' }}>
              {showErrorTxt}
            </h3>
            <div className="popup-buttons">
              <button
                className="confirm-btn"
                onClick={() => {
                  setIsError(false);
                  setShowErrorTxt('');
                  navigate('/point-redeem');
                }}
              >
                {t.coupon_description.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CouponDescription;
